import { type ProspectDto } from "@crowdcoursing/api/openapi";
import { Language, Work } from "@mui/icons-material";
import { Avatar, Box, Paper, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { EmailWithCopyIcon } from "~ui/molecules/EmailWithCopyIcon/EmailWithCopyIcon";
import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import { PROSPECT_TYPES } from "~utils/constants/prospectTypes";
import { STAGES } from "~utils/constants/stages";
import { makeProfileAvatarUrl } from "~utils/url";

const tLastActivity = "Last activity";
const tOwner = "Owner";
const tStage = "Stage";
const tType = "Type";
const tUnknown = "Unknown";
const tContactInformation = "Contact Information";
const tGoToWebsite = "Go to website";

interface ProspectInfoProps {
  prospect: ProspectDto;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  primaryContact?: {
    firstName?: string;
    lastName?: string;
    roleTitle?: string;
  };
  onContactClick?: () => void;
}

export const ProspectInfo = ({
  prospect,
  primaryEmail,
  primaryPhone,
  primaryContact,
  onContactClick,
}: ProspectInfoProps) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Basic Info Section */}
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h5">{prospect?.name ?? ""}</Typography>
        </Box>
        <Typography color="text.secondary" variant="body2">
          {tLastActivity} {DateTime.fromISO(prospect?.updatedAt ?? "").toRelative()}
        </Typography>
      </Box>

      {/* Owner */}
      {(prospect?.ownerFirstName || prospect?.ownerLastName) && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {tOwner}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              alt={[prospect.ownerFirstName, prospect.ownerLastName].filter(Boolean).join(" ")}
              src={prospect.ownerUserId ? makeProfileAvatarUrl(prospect.ownerUserId) : undefined}
              sx={{ height: 24, width: 24 }}
            />
            <Typography>{[prospect.ownerFirstName, prospect.ownerLastName].filter(Boolean).join(" ")}</Typography>
          </Box>
        </Box>
      )}

      {/* Stage & Type */}
      <Box sx={{ display: "grid", gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {tStage}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <span>{STAGES[prospect?.stage ?? 0].emoji}</span>
            <Typography>{STAGES[prospect?.stage ?? 0].name}</Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {tType}
          </Typography>
          <Typography>{PROSPECT_TYPES.find((type) => type.id === prospect?.prospectType)?.name ?? tUnknown}</Typography>
        </Box>
      </Box>

      {/* Contact Information */}
      {(primaryEmail || primaryPhone || primaryContact) && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {tContactInformation}
          </Typography>
          <Paper
            variant="outlined"
            onClick={onContactClick}
            sx={{
              "&:hover": onContactClick
                ? {
                    boxShadow: 1,
                  }
                : {},
              cursor: onContactClick ? "pointer" : "default",
              display: "grid",
              gap: 2,
              p: 2,
            }}
          >
            {/* Contact Name */}
            {primaryContact && (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">
                  {[primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(" ")}
                </Typography>
              </Box>
            )}

            {/* Role */}
            {primaryContact?.roleTitle && (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Work fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {primaryContact.roleTitle}
                </Typography>
              </Box>
            )}

            {/* Contact Info */}
            <Stack spacing={1}>
              {primaryEmail && <EmailWithCopyIcon email={primaryEmail} />}
              {primaryPhone && <PhoneWithCopyIcon phoneNumber={primaryPhone} />}
              {prospect?.website && (
                <Box
                  component="a"
                  href={prospect.website.startsWith("http") ? prospect.website : `https://${prospect.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    alignItems: "center",
                    color: "primary.main",
                    display: "flex",
                    gap: 0.5,
                    textDecoration: "none",
                  }}
                >
                  <Language fontSize="small" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {tGoToWebsite}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
};
